/** Selection maths for voice-sample enrollment.
 *
 * The backend requires 10–60 seconds of usable, single-speaker speech in up to
 * 20 ordered, disjoint ranges. Validating here means the user gets a specific
 * explanation while adjusting the selection, instead of a 400 after waiting for
 * a server-side cut. The server stays authoritative — this only avoids
 * requests that cannot succeed. */

import {
  VoiceSampleRange,
  VoiceTimelineSpan
} from '@utils/types/organization/voice-profile.ts';

/** The API's `Field(max_length=20)` on the ranges list. */
export const MAX_SAMPLE_RANGES = 20;

export const rangeDurationMs = (range: VoiceSampleRange): number =>
  Math.max(0, range.stop_ms - range.start_ms);

export const totalDurationMs = (ranges: VoiceSampleRange[]): number =>
  ranges.reduce((sum, range) => sum + rangeDurationMs(range), 0);

/** Order by start and drop zero-length ranges. */
export const sortRanges = (ranges: VoiceSampleRange[]): VoiceSampleRange[] =>
  [...ranges]
    .filter(range => rangeDurationMs(range) > 0)
    .sort((a, b) => a.start_ms - b.start_ms || a.stop_ms - b.stop_ms);

/** Merge touching or overlapping ranges so the payload stays disjoint.
 *  Adjacent passages from the same speaker are one passage as far as the
 *  extractor is concerned, and merging them also frees range slots. */
export const normalizeRanges = (
  ranges: VoiceSampleRange[]
): VoiceSampleRange[] => {
  const sorted = sortRanges(ranges);
  const merged: VoiceSampleRange[] = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.start_ms <= last.stop_ms) {
      last.stop_ms = Math.max(last.stop_ms, range.stop_ms);
      continue;
    }
    merged.push({ ...range });
  }
  return merged;
};

/** Why a selection cannot be submitted, as a translation key. */
export type SampleSelectionProblem =
  | 'voice-selection-empty'
  | 'voice-selection-too-short'
  | 'voice-selection-too-long'
  | 'voice-selection-too-many-ranges';

export interface SampleSelectionState {
  ranges: VoiceSampleRange[];
  durationMs: number;
  /** Null when the selection is submittable. */
  problem: SampleSelectionProblem | null;
}

/** Validate a selection against the organization's configured limits.
 *  Limits come from the capabilities response, never hardcoded here, so a
 *  deployment that changes them does not need a frontend release. */
export const evaluateSelection = (
  ranges: VoiceSampleRange[],
  minSeconds: number,
  maxSeconds: number
): SampleSelectionState => {
  const normalized = normalizeRanges(ranges);
  const durationMs = totalDurationMs(normalized);
  let problem: SampleSelectionProblem | null = null;
  if (!normalized.length) problem = 'voice-selection-empty';
  else if (normalized.length > MAX_SAMPLE_RANGES)
    problem = 'voice-selection-too-many-ranges';
  else if (durationMs < minSeconds * 1000)
    problem = 'voice-selection-too-short';
  else if (durationMs > maxSeconds * 1000) problem = 'voice-selection-too-long';
  return { ranges: normalized, durationMs, problem };
};

/** Group a timeline into one entry per original diarization label.
 *  One label has many turns; the assignment list is per label, not per turn
 *  (issue #2355: "Group source timeline spans by their original
 *  `speaker_label` before building the assignment list"). */
export interface TimelineLabel {
  speaker_label: string;
  spans: VoiceTimelineSpan[];
  /** First and last moment this voice is heard. */
  firstStartMs: number;
  lastStopMs: number;
  /** Summed speech, which is what the 10–60s requirement measures. */
  totalMs: number;
}

export const groupTimelineByLabel = (
  timeline: VoiceTimelineSpan[]
): TimelineLabel[] => {
  const byLabel = new Map<string, VoiceTimelineSpan[]>();
  for (const span of timeline) {
    const spans = byLabel.get(span.speaker_label);
    if (spans) spans.push(span);
    else byLabel.set(span.speaker_label, [span]);
  }
  return [...byLabel.entries()]
    .map(([speaker_label, spans]) => {
      const ordered = [...spans].sort((a, b) => a.start_ms - b.start_ms);
      return {
        speaker_label,
        spans: ordered,
        firstStartMs: ordered[0].start_ms,
        lastStopMs: Math.max(...ordered.map(span => span.stop_ms)),
        totalMs: ordered.reduce(
          (sum, span) => sum + Math.max(0, span.stop_ms - span.start_ms),
          0
        )
      };
    })
    .sort((a, b) => a.firstStartMs - b.firstStartMs);
};

/** The original diarization label heard at a moment in the source audio.
 *
 * This is the bridge between editor rows and identity assignment: the document
 * stores display names, which the user may already have renamed, so position in
 * the source audio is the only reliable way back to the label the backend keys
 * decisions on. Transcript utterances and caption segments both carry
 * millisecond timings, matching the timeline's units.
 *
 * Prefers the span that contains the moment; falls back to the span with the
 * largest overlap with [startMs, stopMs], which keeps a row resolvable when
 * editing has nudged its boundaries off the original diarization. */
export const resolveSpeakerLabelAt = (
  timeline: VoiceTimelineSpan[],
  startMs: number,
  stopMs?: number
): string | null => {
  if (!timeline.length) return null;
  const end = stopMs != null && stopMs > startMs ? stopMs : startMs;

  // A window belongs to the label with the most speech in it. Spans from
  // different voices overlap in real recordings, and a span runs from the
  // first to the last word of a source entry, so a long turn's span covers
  // the short turns that interrupt it.
  if (end > startMs) {
    const scores = new Map<string, { overlap: number; narrowest: number }>();
    for (const span of timeline) {
      const overlap =
        Math.min(end, span.stop_ms) - Math.max(startMs, span.start_ms);
      if (overlap <= 0) continue;
      const width = span.stop_ms - span.start_ms;
      const current = scores.get(span.speaker_label);
      if (current) {
        current.overlap += overlap;
        current.narrowest = Math.min(current.narrowest, width);
      } else {
        scores.set(span.speaker_label, { overlap, narrowest: width });
      }
    }
    let best: { label: string; overlap: number; narrowest: number } | null =
      null;
    for (const [label, score] of scores) {
      const better =
        !best ||
        score.overlap > best.overlap ||
        (score.overlap === best.overlap && score.narrowest < best.narrowest);
      if (better) best = { label, ...score };
    }
    if (best) return best.label;
  }

  // A bare moment belongs to the NARROWEST span containing it, which is the
  // most specific answer available. Taking the first containing span handed a
  // short turn the surrounding voice's label, and the identity decision then
  // overwrote the one already recorded for that voice (#2371).
  const containing = timeline.filter(
    span => startMs >= span.start_ms && startMs <= span.stop_ms
  );
  if (containing.length) {
    const narrowest = containing.reduce((best, span) =>
      span.stop_ms - span.start_ms < best.stop_ms - best.start_ms ? span : best
    );
    return narrowest.speaker_label;
  }

  // Nothing overlaps (an inserted row, or timings edited past any source
  // span): fall back to the nearest span so the action is still available.
  const nearest = timeline.reduce((closest, span) => {
    const distance = Math.min(
      Math.abs(span.start_ms - startMs),
      Math.abs(span.stop_ms - startMs)
    );
    const closestDistance = Math.min(
      Math.abs(closest.start_ms - startMs),
      Math.abs(closest.stop_ms - startMs)
    );
    return distance < closestDistance ? span : closest;
  }, timeline[0]);
  return nearest.speaker_label;
};

/** Was this display name left by diarization or set by a human?
 *
 * Auto-apply of a strong recognition match must never overwrite a correction.
 * "SPEAKER_00", "Speaker 1", a bare "3", "No name" and an empty label all mean
 * "no human has named this voice yet"; anything else is a conscious choice —
 * either a directly typed name or a directory pick — and is left alone.
 *
 * The current `speakerLabel` for the row is matched case-insensitively so
 * raw diarization identifiers also count as uncorrected. */
export const isHumanCorrectedSpeakerName = (
  currentName: string,
  speakerLabel?: string | null
): boolean => {
  const trimmed = currentName.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'no name') return false;
  if (/^speaker[_\s]*\d+$/i.test(trimmed)) return false;
  if (/^\d+$/.test(trimmed)) return false;
  if (speakerLabel && lower === speakerLabel.trim().toLowerCase()) return false;
  return true;
};

/** Does one editor row belong to that original diarization label?
 *
 * Naming a voice has to be scoped to the detected voice, not to whatever
 * display name the row happens to carry: two rows that share a display name
 * are not necessarily the same person, so a name-matched sweep reaches the
 * other speaker's rows and the second assignment eats the first (#2371).
 *
 * Rows with no timing cannot be placed in the source audio, so they are left
 * alone rather than renamed on a guess. */
export const rowBelongsToLabel = (
  timeline: VoiceTimelineSpan[],
  speakerLabel: string,
  startMs?: number,
  stopMs?: number
): boolean => {
  if (startMs == null || !timeline.length) return false;
  return resolveSpeakerLabelAt(timeline, startMs, stopMs) === speakerLabel;
};

/** "1:05" / "12:04:03" for a duration in milliseconds. */
export const formatRangeClock = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

/** "18s" / "1m 24s" for a selected duration. */
export const formatSelectionDuration = (ms: number): string => {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
};
